import { defineStore, acceptHMRUpdate } from 'pinia'
import axios from "axios";
import { useRestapi, handle401 } from "./restapi";
import { attachUninterceptedErrorReporting, markApiErrorHandled } from "../services/apiErrorReporting";
import { toStoreError } from "../services/apiClientError";
import { useSessionStore } from "./session";
import { Cookies, Notify } from "quasar";
import { useRouter } from "vue-router";
import { ref, unref, computed } from "vue";
import { router as routerInstance } from "../router";


export const useRegionStore = defineStore('region', () => {
  const sessionStore = useSessionStore();

  // Expose organization from session store
  const org = computed(() => sessionStore.organization);
  const api_instance = ref(null);
  const api_region = ref(null);
  const session_object = ref(null);
  const activity = ref(null);
  const course = ref(null);
  const copied_rubric = ref(null);

  // Tracks course uids that were just soft-deleted by this client. Course
  // listings are served from OpenSearch, which is updated asynchronously via a
  // DynamoDB stream, so a freshly deleted course can still appear in a list
  // refetched immediately after deletion. We filter these uids out of GetCourses
  // results until the search index catches up. See issue #760.
  const recentlyDeletedCourseUids = ref(new Set());

  const RestAPI = useRestapi()
  axios.defaults.withCredentials = true;

  console.log(import.meta.env.VITE_API_URL);

  const default_api = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  default_api.interceptors.response.use(RestAPI.intercept, RestAPI.error_intercept);

  // Currently unused, but wired for reporting-only triage so it can't become a
  // silent hole the moment someone reaches for it.
  const no_intercept = attachUninterceptedErrorReporting(axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      'Content-Type': 'application/json'
    }
  }));

  // Track pending session request to prevent concurrent calls
  let sessionPromise = null;

  const session = async () => {
    // If there's already a pending session request, return that promise
    if (sessionPromise) {
      console.debug('Session request already pending, reusing existing promise');
      return sessionPromise;
    }

    // If we already have session data, return it immediately
    if (session_object.value) {
      console.debug('Session already loaded, returning cached data');
      return {
        user: session_object.value.user,
        organization: session_object.value.organization
      };
    }

    // Delegate to sessionStore.fetchSession() to avoid duplicate /user_session calls
    sessionPromise = (async () => {
      try {
        const sessionData = await sessionStore.fetchSession();
        session_object.value = sessionData;
        return {
          user: sessionData.user,
          organization: sessionData.organization
        };
      } catch (error) {
        if (error && error.response && error.response.status === 401) {
          // Route through the shared central 401 handler so this site gets the
          // same navigate-then-clear ordering, the redirect_to back-link (this
          // used to push a bare '/login' with no redirect_to — a contract
          // violation), and the shared re-entrancy latch. handle401 returns a
          // rejected promise, so returning it propagates the failure to callers
          // exactly as the old `throw error` did.
          return handle401(error, {
            setOrganization,
            clearSession: sessionStore.clear_session
          });
        }
        console.log(error);
        throw error;
      } finally {
        sessionPromise = null;
      }
    })();

    return sessionPromise;
  };

  const api = async () => {
    if (!session_object.value) {
      await session();
    }
    if (!org.value) {
      // System-level roles (Superuser, Support, Content Manager) don't need an organization
      const userRoles = session_object.value?.user?.roles || [];
      const systemRoles = ['Superuser', 'Support', 'Content Manager'];
      const isSystemUser = userRoles.some(role => systemRoles.includes(role));
      if (!isSystemUser) {
        // sessionStore.fetchSession() caches the session once user.value is
        // set and returns the cached organization (even if null) on every
        // later non-forced call. Some signup-stepper paths can leave that
        // cache stale — organization null in memory even though the backend
        // session already has one (see issue #822) — so force one fresh
        // fetch here before concluding the user truly has no organization.
        // fetchSession uses an un-intercepted axios instance, so nothing above
        // this call turns its 401 into the app's standard expiry behaviour.
        // Left bare, an expired session here produced no redirect, no session
        // clear and no report — the user just watched the page fail. Route it
        // through the same central handler session() uses so this site gets the
        // redirect_to back-link, the navigate-then-clear ordering and the
        // shared re-entrancy latch. handle401 returns a rejected promise, so
        // returning it propagates the failure to api()'s callers.
        let refreshed;
        try {
          refreshed = await sessionStore.fetchSession(true);
        } catch (error) {
          if (error?.response?.status === 401) {
            return handle401(error, {
              setOrganization,
              clearSession: sessionStore.clear_session
            });
          }
          throw error;
        }
        if (!refreshed.organization) {
          if (routerInstance) {
            routerInstance.push('/signup');
          }
          return;
        }
      }
    }
     return default_api;
  };

  const GetOrg = async (uid) => {
    const response = await default_api.get(`/organization/${uid}`);
    // A non-200 2xx (axios only resolves 2xx by default) has no
    // organization payload. Silently returning undefined here used to flow
    // straight into sessionStore.organization, and every reactive consumer
    // (e.g. OrganizationView's delete dialog reading organization.name)
    // could then throw on the next render (issue #1189/#1076). Fail loudly
    // instead so error_intercept/Error Tracking see it and callers' .catch
    // runs, leaving the current organization untouched.
    if (response.status !== 200 || !response.data.organization) {
      throw new Error(`GetOrg: unexpected response for organization ${uid} (status ${response.status})`);
    }
    return response.data.organization;
  };

  const GetOrganization = async (uid) => {
    const organization = await GetOrg(uid);
    sessionStore.organization = organization;
  };

  async function UpdateOrganization(org_in) {
    const local_api = await api();
    const response = await local_api.post(`/organization`, { organization: org_in });
    // Same rule as GetOrg: never overwrite the session organization with an
    // undefined/missing payload. Throw so the caller's .catch handles it and
    // the current organization is left alone (issue #1189/#1076).
    if (!response.data || !response.data.organization) {
      throw new Error('UpdateOrganization: response missing organization');
    }
    sessionStore.organization = response.data.organization;
    Notify.create({
      color: 'positive',
      message: `Organization ${org.name} updated successfully`,
      icon: 'save'
    });
  }

  const GetPurchases = async (org_uid, entity_type, entity_uid, after) => {
    const api_instance = await api();
    let url = `/organization/${org_uid}`
    if (entity_type !== 'organization') {
      url += `/${entity_type}/${entity_uid}`
    }
    const response = await api_instance.get(`${url}/purchases`, { params: { after: after } });
    if (response.status === 200) {
      return response.data;
    }
  };

  const MakePurchase = async (org, purchase) => {
    const api_instance = await api();
    // `mayHaveCompleted: true` - a dropped response doesn't mean the purchase
    // didn't go through with Stripe; restapi.js's error_intercept uses the
    // softer "this may have been saved" wording instead of "nothing was saved".
    const response = await api_instance.post(`/organization/${org.uid}/purchase`, { purchase: purchase }, { mayHaveCompleted: true });
    Notify.create({
      color: 'info',
      message: `Purchase for ${org.name}`,
      icon: 'shopping_cart'
    });
    // Never silently blank the session organization (issue #1189/#1076) --
    // leave it as it was if the response is missing one.
    if (response.data && response.data.organization) {
      sessionStore.organization = response.data.organization;
    }
    if (response.status === 200) {
      return response.data;
    }
  };

  const GetQuote = async (org, purchase) => {
    const api_instance = await api();
    const response = await api_instance.post(`/organization/${org.uid}/quote`, { purchase: purchase });
    Notify.create({
      color: 'info',
      message: `Quote for ${org.name}`,
      icon: 'shopping_cart'
    });
    if (response.status === 200) {
      return response.data;
    }
  };

  const DeleteOrganization = async (org) => {
    const api_instance = await api();
    const response = await api_instance.delete(`/organization/${org.uid}`);
    Notify.create({
      color: 'info',
      message: `Organization ${org.name} deleted successfully`,
      icon: 'delete'
    });
  };

  const GetOrgLoginOptions = async (subdomain, returnTo) => {
    const params = returnTo ? { return_to: returnTo } : {}
    const response = await default_api.get(`/organization/${subdomain}/login_options`, { params });
    if (response.status === 200) {
      // Never silently blank the session organization (issue #1189/#1076) --
      // leave it as it was if the response is missing one. Login.vue's
      // caller already treats a rejected/incomplete lookup as "no subdomain
      // org" and falls through to the normal login form.
      if (response.data && response.data.organization) {
        sessionStore.organization = response.data.organization;
      }
      return response.data;
    }
  };

  const ParseSAML = async (metadata_url) => {
    const response = await default_api.post(`/parse_saml_metadata`, { metadata_url: metadata_url });
    if (response.status === 200) {
      return response.data;
    }
  };

  const UpdateProgram = async (program) => {
    const api_instance = await api();
    const response = await api_instance.post(`/organization/${org.value.uid}/program`, { program: program });
    Notify.create({
      color: 'positive',
      message: `Program ${response.data.program.program_name} updated successfully`,
      icon: 'save'
    });
    return response.data.program;
  };

  const DeleteProgram = async (program) => {
    const api_instance = await api();
    const response = await api_instance.delete(`/organization/${org.value.uid}/program/${program.uid}`);
    Notify.create({
      color: 'info',
      message: `Program ${program.program_name} deleted successfully`,
      icon: 'delete'
    });
  };

  const UpdateCampus = async (campus) => {
    const api_instance = await api();
    const response = await api_instance.post(`/organization/${org.value.uid}/campus`, { campus: campus });
    Notify.create({
      color: 'positive',
      message: `Campus ${response.data.campus.campus_name} updated successfully`,
      icon: 'save'
    });
    return response.data.campus;
  };

  const DeleteCampus = async (campus) => {
    const api_instance = await api();
    const response = await api_instance.delete(`/organization/${org.value.uid}/campus/${campus.uid}`);
    Notify.create({
      color: 'info',
      message: `Campus ${campus.campus_name} deleted successfully`,
      icon: 'delete'
    });
  };

  const GetProgram = async (program_uid) => {
    const api_instance = await api();
    const response = await api_instance.get(`/organization/${org.value.uid}/program/${program_uid}`);
    if (response.status === 200) {
      return response.data.program;
    }
  };

  const GetCampus = async (campus_uid) => {
    const api_instance = await api();
    const response = await api_instance.get(`/organization/${org.value.uid}/campus/${campus_uid}`);
    if (response.status === 200) {
      return response.data.campus;
    }
  };

  const GetCampuses = async (page, rowsPerPage, sortBy, descending, filter, organization_uid) => {
    const api_instance = await api();
    const org_uid = organization_uid || org.value?.uid;
    if (!api_instance || !org_uid) {
      // org can legitimately be gone by the time this resolves: a sibling
      // request in the same Promise.all as Course.vue's initCourse() (e.g.
      // GetCourse) can 401 first, and handle401 clears the session while this
      // call is still awaiting api(). The expiry flow is already redirecting
      // to /login, so this request is moot rather than broken — see the
      // identical guard on GetPrograms below and its regression test.
      return;
    }
    const response = await api_instance.get(`/organization/${org_uid}/campuses`, {
      params: {
        page,
        rowsPerPage,
        sortBy,
        descending,
        filter
      }
    });
    if (response.status === 200) {
      return response.data.campuses;
    }
  };

  const GetCabinets = async (page, rowsPerPage, sortBy, descending, filter) => {
    const api_instance = await api();
    const response = await api_instance.get(`/cabinet/organization/${org.value.uid}`, {
      params: {
        page,
        rowsPerPage,
        sortBy,
        descending,
        query: filter
      }
    });
    if (response.status === 200) {
      return response.data;
    }
  };

  const CreateCabinet = async (name, campus_uid) => {
    const api_instance = await api();
    const response = await api_instance.post('/cabinet', {
      organization_uid: org.value.uid,
      name,
      campus_uid: campus_uid || null,
      drawers: []
    });
    if (response.status === 200) {
      return response.data.cabinet;
    }
  };

  const DeleteCabinet = async (cabinet_uid) => {
    const api_instance = await api();
    const response = await api_instance.delete(`/cabinet/${cabinet_uid}`);
    return response.status === 200;
  };

  const CopyCabinet = async (cabinet_uid, copy_stock = false) => {
    const api_instance = await api();
    const response = await api_instance.post(`/cabinet/${cabinet_uid}/copy`, { copy_stock });
    if (response.status === 200) {
      Notify.create({
        color: 'positive',
        message: `Cabinet ${response.data.cabinet.name} copied successfully`,
        icon: 'save'
      });
      return response.data.cabinet;
    }
  };

  async function GetPrograms(page, rowsPerPage, sortBy, descending, filter, organization_uid) {
    const api = await this.api();
    const org_uid = organization_uid || this.org?.uid;
    if (!api || !org_uid) {
      // Sibling of the this.org.programs guard below, for the same race: org
      // can already be gone by the time we get here (session expired
      // mid-flight — see that comment and region.getProgramsOrgRace.test.js).
      // This one crashed instead of degrading because it read this.org.uid
      // while building the URL, before the write below ever ran.
      return;
    }
    const response = await api.get(`/organization/${org_uid}/programs`, {
      params:
        { query: filter, page: page, rowsPerPage: rowsPerPage, sortBy: sortBy, descending: descending }
    });
    if (response.status === 200) {
      // this.org can go null mid-flight if the session expires while this
      // request is in-flight (handle401 clears it after navigating to
      // /login) — a legitimate race, not a sign anything upstream is broken.
      // The fetched programs are still returned to the caller either way.
      if (this.org && (!organization_uid || organization_uid === this.org.uid)) {
        this.org.programs = response.data.programs;
      }
      return response.data.programs;
    }
  }

  async function GetCourses(organization, program_uid, campus_uid, page, rowsPerPage, sortBy, descending, filter, show_old, only_instructor) {
    const api = await this.api();
    const response = await api.get(`/courses`, {
      params:
      {
        organization_uid: organization,
        campus_uid: campus_uid,
        program_uid: program_uid,
        query: filter,
        page: page,
        rowsPerPage: rowsPerPage,
        sortBy: sortBy,
        descending: descending,
        show_old: show_old,
        only_instructor: only_instructor
      }
    });
    if (response.status === 200) {
      const data = response.data;
      // Filter out courses this client just deleted but that the search index
      // may not yet reflect (see recentlyDeletedCourseUids / issue #760).
      if (recentlyDeletedCourseUids.value.size && Array.isArray(data.courses)) {
        const before = data.courses.length;
        data.courses = data.courses.filter(
          (c) => !recentlyDeletedCourseUids.value.has(c.uid)
        );
        const removed = before - data.courses.length;
        if (removed > 0 && typeof data.total === 'number') {
          data.total = Math.max(0, data.total - removed);
        }
      }
      return data;
    }
  }

  async function get_product_prices_for_user(org_uid, program_uid, campus_uid, cohort_uid) {
    const api = await this.api();
    const response = await api.get('/user/product_prices', { params: { organization_uid: org_uid, program_uid: program_uid, campus_uid: campus_uid, cohort_uid: cohort_uid } });
    if (response.status === 200) {
      return response.data;
    }
  }

  async function GetTemplateCourses(page, rowsPerPage, sortBy, descending, filter, filtering) {
    try {
      const api = await this.api();
      const response = await api.get(`/template_courses`, {
        params:
        {
          query: filter,
          page: page,
          rowsPerPage: rowsPerPage,
          sortBy: sortBy,
          descending: descending,
          filtering: filtering
        }
      });
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetActivities(course, page, rowsPerPage, sortBy, descending, filter) {
    try {
      const api = await this.api();
      const response = await api.get(`/activities`, {
        params:
          { course_uid: course, query: filter, page: page, rowsPerPage: rowsPerPage, sortBy: sortBy, descending: descending }
      });
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetTemplateActivities(page, rowsPerPage, sortBy, descending, filter, filtering, course_uid) {
    try {
      const api = await this.api();
      const response = await api.get(`/template_activities`, {
        params:
          { query: filter, page: page, rowsPerPage: rowsPerPage, sortBy: sortBy, descending: descending, filtering: filtering, course_uid: course_uid }
      });
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function UpdateCourse(course) {
    const api = await this.api();
    const response = await api.post(`/course`, { course: course });
    Notify.create({
      color: 'positive',
      message: `Course ${response.data.course.course_name} updated successfully`,
      icon: 'save'
    })

    return response.data;
  }

  async function DeleteCourse(course) {
    const api = await this.api();
    const response = await api.delete(`/course/${course.uid}`)
    // Remember this deletion so list views can hide the course immediately,
    // even before the (asynchronously updated) search index reflects it.
    if (course && course.uid) {
      recentlyDeletedCourseUids.value.add(course.uid)
    }
    // Refresh session so the user's embedded course list is updated. The
    // course delete itself already succeeded above, so a transient network
    // failure here (already reported by fetchSession's own un-intercepted
    // error reporting — see apiErrorReporting.js) must not stop the success
    // notification or the caller's navigation away from the deleted course.
    const { useSessionStore } = await import('./session')
    const sessionStore = useSessionStore()
    try {
      await sessionStore.fetchSession()
    } catch (error) {
      console.warn('Could not refresh session after course deletion:', error)
    }
    Notify.create({
      color: 'info',
      message: `Course ${course.course_name} deleted successfully`,
      icon: 'delete'
    })
    return response.data
  }

  async function UpdateActivity(activity, msg) {
    const api = await this.api();
    const response = await api.post(`/activity`, { activity: activity })
    Notify.create({
      color: 'positive',
      message: msg ? msg : `Activity ${response.data.activity.activity_name} updated successfully`,
      icon: 'save'
    })
    // has_student_data comes back as a sibling of activity, not a persisted
    // field on it (see get_activity/update_activity/copy_template_activity
    // handlers) — bolt it onto the returned object so callers that replace
    // their local activity ref wholesale (activity.value = a) don't drop it.
    const updated = response.data.activity;
    updated.has_student_data = response.data.has_student_data;
    return updated;
  }

  // Focused endpoint for toggling just an activity's visibility — usable by
  // Adjuncts, who are not permitted to call UpdateActivity's full edit
  // surface (see update_activity_visibility.py, issue #781 follow-up).
  async function UpdateActivityVisibility(courseUid, uid, visibility, activityStartDate) {
    const api = await this.api();
    const body = { visibility }
    if (activityStartDate !== undefined) body.activity_start_date = activityStartDate
    const response = await api.put(`/course/${courseUid}/activity/${uid}/visibility`, body)
    const activity = response.data.activity;
    // Save feedback, matching UpdateActivity. The Activities list toggle used to
    // call UpdateActivity and got its toast for free; routing through this
    // scoped endpoint (7c487fe3) dropped it. The Adjunct auto-save watcher on
    // the activity detail page never had one at all. Without this both persist
    // silently, so the user has no confirmation the toggle stuck.
    Notify.create({
      color: 'positive',
      message: `Activity ${activity.activity_name} is now ${activity.visibility ? 'visible' : 'hidden'}`,
      icon: 'save'
    })
    return activity;
  }

  async function DeleteActivity(activity) {
    const api = await this.api();
    const response = await api.delete(`/course/${activity.course_uid}/activity/${activity.uid}`)
    Notify.create({
      color: 'info',
      message: `Activity ${activity.activity_name} deleted successfully`,
      icon: 'delete'
    })

  }

  async function SubmitActivity(courseId, activityId, submissionData) {
    try {
      const api = await this.api();
      const response = await api.post(`/course/${courseId}/activity/${activityId}/submit`, submissionData);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      throw toStoreError(error);
    }
  }



  async function StartNewAttempt(courseId, activityId) {
    try {
      const api = await this.api();
      const response = await api.post(`/course/${courseId}/activity/${activityId}/attempts`);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      throw toStoreError(error);
    }
  }

  async function ValidateSubmission(courseId, activityId, attemptNumber) {
    try {
      const api = await this.api();
      const response = await api.post(`/course/${courseId}/activity/${activityId}/validate-submission`, {
        attempt_number: attemptNumber
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      throw toStoreError(error);
    }
  }

  async function AutoSaveActivity(courseId, activityId, activityData) {
    try {
      const api = await this.api();
      const response = await api.post(`/course/${courseId}/activity/${activityId}/auto-save`, {
        activity_data: activityData,
        auto_saved_at: new Date().toISOString()
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      // Don't throw errors for auto-save failures to avoid disrupting user experience
      console.warn('Auto-save failed:', error);
      return null;
    }
  }

  async function GetSubmissionStatus(courseId, activityId) {
    try {
      const api = await this.api();
      const response = await api.get(`/course/${courseId}/activity/${activityId}/submission-status`);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetAttemptInfo(courseId, activityId) {
    try {
      const api = await this.api();
      const response = await api.get(`/course/${courseId}/activity/${activityId}/attempt-info`);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetCourse(course_uid) {
    try {
      const api = await this.api();
      const response = await api.get(`/course/${course_uid}`);
      if (response.status === 200) {
        return response.data.course;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetActivity(course_uid, activity_uid) {
    try {
      const api = await this.api();
      const response = await api.get(`/course/${course_uid}/activity/${activity_uid}`);
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function CopyActivity(activity_uid) {
    const api = await this.api();
    const response = await api.post(`/copy_template_activity`, { activity_uid: activity_uid })
    Notify.create({
      color: 'positive',
      message: `Activity ${response.data.activity.activity_name} copied successfully`,
      icon: 'save'
    })
    // Set store refs so Activity.vue skips the GET on mount (avoids eventual consistency).
    // _skip_next_fetch is an explicit, one-time signal that this data is
    // known-fresh — Activity.vue consumes and deletes it on its next load.
    // Without an explicit flag, Activity.vue can't tell "freshly written by
    // a create/copy action" apart from "leftover from an earlier, unrelated
    // visit to this same activity" just by comparing uids.
    activity.value = response.data.activity;
    activity.value._skip_next_fetch = true;
    activity.value.has_student_data = response.data.has_student_data;
    // Stash answer key UIDs on the activity so the view can use them without a fetch
    activity.value._answer_key_template_patient_uids = response.data.answer_key_template_patient_uids || [];
    course.value = response.data.course;
    return response.data;
  }

  async function CopyCourse(course_uid) {
    const api = await this.api();
    const response = await api.post(`/copy_template_course`, { course_uid: course_uid })
    Notify.create({
      color: 'positive',
      message: `Course ${response.data.course.course_name} copied successfully`,
      icon: 'save'
    })
    course.value = response.data.course;
    return response.data.course;
  }

  async function MakeTemplateActivity(activity, course_uid) {
    const api = await this.api();
    const response = await api.post(`/make_template_activity/${course_uid}/${activity.uid}`)
    Notify.create({
      color: 'positive',
      message: `Activity ${response.data.activity.activity_name} made into template successfully`,
      icon: 'save'
    })
    return response.data.activity;
  }
  async function MakeTemplateCourse(course) {
    const api = await this.api();
    const response = await api.post(`/make_template_course/${course.uid}`)
    Notify.create({
      color: 'positive',
      message: `Course ${response.data.course.course_name} made into template successfully`,
      icon: 'save'
    })
    return response.data.course;
  }

  async function AddCourse(course, campus_uid, program_uid) {
    const api = await this.api();
    const response = await api.post(`/add_template_course/${course.uid}`, { program_uid: program_uid, campus_uid: campus_uid })
    Notify.create({
      color: 'positive',
      message: `Course ${response.data.course.course_name} added successfully`,
      icon: 'save'
    })
    return response.data;
  }

  async function GetStudentActivity(course_uid, activity_uid) {
    try {
      const api = await this.api();
      const response = await api.get(`/course/${course_uid}/student_activity/${activity_uid}`);
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetStudentSubmissions(course_uid, activity_uid) {
    try {
      const api = await this.api();
      const response = await api.get(`/course/${course_uid}/activity/${activity_uid}/submissions`);
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetInstructorSubmissions(course_uid, activity_uid, page, rowsPerPage, sortBy, descending, filter) {
    try {
      const api = await this.api();
      const response = await api.get(`/course/${course_uid}/activity/${activity_uid}/instructor/submissions`, {
        params: { page: page, rowsPerPage: rowsPerPage, sortBy: sortBy, descending: descending, query: filter }
      });
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function DownloadInstructorSubmissionsCSV(course_uid, activity_uid) {
    try {
      const api = await this.api();
      const response = await api.get(`/course/${course_uid}/activity/${activity_uid}/instructor/submissions/csv`, {
        responseType: 'text'
      });
      return response.data;
    }
    catch (error) {
      // DELIBERATE EXCEPTION to this file's reject-on-4xx contract, and one of
      // only two left (the other is RetryLTIGrade). `null` is not the
      // type-confusion bug the contract exists to prevent: the success value is
      // CSV text, so a falsy result is unambiguously "no CSV" and no caller can
      // mistake it for one. Converting would only force a try/catch on a
      // download button for no behavioural gain.
      //
      // If you convert this, the caller's falsy check in Submissions.vue must
      // become a catch IN THE SAME CHANGE. Do not delete consumer guards for a
      // function you have not converted — that is exactly how the
      // InvalidateAttempt false-success regression happened.
      if (error.response && error.response.status >= 400 && error.response.status < 500) return null;
      throw error;
    }
  }

  async function ReopenAssignment(course_uid, activity_uid, data) {
    try {
      const api = await this.api();
      const response = await api.post(`/course/${course_uid}/activity/${activity_uid}/instructor/reopen`, data);
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function BulkReopenAssignments(course_uid, activity_uid, data) {
    try {
      const api = await this.api();
      const response = await api.post(`/course/${course_uid}/activity/${activity_uid}/instructor/bulk-operations`, data);
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function GrantAdditionalAttempts(course_uid, activity_uid, user_uid, data) {
    try {
      const api = await this.api();
      const response = await api.post(`/course/${course_uid}/activity/${activity_uid}/user/${user_uid}/grant-attempts`, data);
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function RetryLTIGrade(course_uid, activity_uid, user_uid, session_id = null) {
    try {
      const api = await this.api();

      // Prepare request body with session_id if available
      const requestBody = {};
      if (session_id) {
        requestBody.session_id = session_id;
      }

      const response = await api.post(`/course/${course_uid}/activity/${activity_uid}/users/${user_uid}/retry-lti-grade`, requestBody);
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      // DELIBERATE EXCEPTION to this file's reject-on-4xx contract, and one of
      // only two left (the other is DownloadInstructorSubmissionsCSV). A failed
      // LTI grade retry is a RESULT, not an error: the backend answers a 4xx
      // with a structured { retry_success, ... } body describing what it tried,
      // and Submissions.vue renders that per-student. This is already the
      // discriminated-result shape the contract asks for — an object on both
      // paths — so there is no type confusion to fix.
      //
      // If you convert this, both call sites in Submissions.vue (single and
      // bulk retry) must move to a catch IN THE SAME CHANGE. Do not delete
      // consumer guards for a function you have not converted — that is exactly
      // how the InvalidateAttempt false-success regression happened.
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        // For retry endpoints, check if the response contains detailed retry information
        if (error.response.data && typeof error.response.data === 'object' && 'retry_success' in error.response.data) {
          // This is a structured retry response, return it as-is
          return error.response.data;
        } else {
          // This is a generic error response
          const errorMessage = error.response.data?.error || error.response.data?.message || 'Unknown error occurred';
          return { success: false, error_message: errorMessage };
        }
      }
      throw error;
    }
  }

  async function InvalidateAttempt(course_uid, activity_uid, user_uid, attempt_number, data) {
    try {
      const api = await this.api();
      const response = await api.post(`/course/${course_uid}/activity/${activity_uid}/user/${user_uid}/attempt/${attempt_number}/invalidate`, data);
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      // Handle different types of errors with specific messages
      if (error.response) {
        const status = error.response.status;

        // Rejects like the rest of this file. This used to RESOLVE the message
        // string, which meant useInstructorAttempts' invalidateAttempt fell
        // through to its "Successfully invalidated attempt N" toast on a
        // refusal — the instructor saw a green success and a red reason at
        // once while the attempt stayed valid.
        if (status >= 400 && status < 500) {
          throw toStoreError(error);
        }

        // Server errors (5xx)
        if (status >= 500) {
          throw new Error('Server error - please try again later');
        }
      }

      // Network or other errors
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        throw new Error('Network error - please check your connection');
      }

      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Request timeout - please try again');
      }

      // Re-throw other errors
      throw error;
    }
  }

  async function BulkGrantAttempts(course_uid, activity_uid, data) {
    try {
      const api = await this.api();
      const response = await api.post(`/course/${course_uid}/activity/${activity_uid}/instructor/bulk-operations`, {
        operation: 'grant_extra_attempts',
        ...data
      });
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetGradingReport(course_uid, activity_uid, patient_uid, student_uid, params = {}) {
    try {
      const api = await this.api();
      const response = await api.get(`/course/${course_uid}/activity/${activity_uid}/patient/${patient_uid}/grading-report`, {
        params: {
          student_uid: student_uid,
          attempt: params.attempt || 'latest'
        }
      });
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function SaveFieldScore(data) {
    try {
      const api = await this.api();
      const response = await api.post('/field-score', data);
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetFieldScores(course_uid, activity_uid, user_uid, patient_uid, attempt_number) {
    try {
      const api = await this.api();
      const response = await api.get(`/field-score/${course_uid}/${activity_uid}/${user_uid}/${patient_uid}/${attempt_number}`);
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetSimulationData(course_uid, activity_uid) {
    try {
      const api = await this.api();
      const response = await api.get(`/course/${course_uid}/activity/${activity_uid}/simulation_data`);
      if (response.status === 200) {
        return response.data;
      }
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetStudentDashboardActivities(page = 1, pageSize = 10) {
    try {
      const api = await this.api();
      // this.api() legitimately resolves to undefined when a forced session
      // refetch confirms the user genuinely has no organization — it redirects
      // to /signup and returns nothing by design (see region.apiOrgRace.test.js,
      // issue #822). Guard against it here so that edge case throws a clear,
      // catchable error instead of crashing on `.get` of undefined. One call
      // site; the other ~83 in this file are tracked under #966.
      //
      // Stamped HANDLED rather than thrown bare, for the reason handle401 stamps
      // its 401: by the time this throws, api() has ALREADY pushed the visitor to
      // /signup, so the app has explained itself and the view has nothing to add.
      // Unstamped, StudentDashboard.vue's `if (!isApiErrorHandled(error))` gate
      // opens and the visitor gets an "Error loading courses" toast on top of a
      // page that is navigating away, while RUM's console.error instrumentation
      // mints a fresh Error Tracking issue at the same volume as the TypeError
      // this guard removed (Datadog 0390a3de; the #895 duplicate-report problem
      // arriving by a different door). See StudentDashboard.noOrg.test.js.
      //
      // The catch below still routes this through toStoreError(), but that's
      // safe: toStoreError only transforms a rejection with an axios 4xx
      // `.response` (isClientError), and this synthesized Error has none, so
      // it passes through toStoreError unchanged — the markApiErrorHandled
      // stamp survives intact.
      if (!api) {
        throw markApiErrorHandled(
          new Error('Unable to load student dashboard activities: no organization context is available')
        );
      }
      const response = await api.get(`/student_dashboard_activities`, {
        params: { page, page_size: pageSize }
      });

      return response.data;
    }
    catch (error) {
      throw toStoreError(error);
    }
  }

  async function AddOrInviteUsers(users, role, entity) {
    let api;
    if (entity.entity_type === 'organization') {
      api = default_api;
    }
    else {
      api = await this.api();
    }
    const response = await api.post('/add_invite_users', { users: users, role: role, entity: entity });
    if (response.status === 200) {
      Notify.create({
        color: 'positive',
        message: `Added/Invited ${response.data.users.length} users successfully`,
        icon: 'save'
      })

      return withLicensingProgress(response.data);
    }
  }

  // Since #959 the licences for a roster are granted by a background worker, so
  // the add/invite response carries two extra keys beside the users: the jobs
  // doing the granting (`licensing_jobs`, to poll) and who they owe a licence to
  // (`licensing_pending`, so a freshly added student reads as pending rather
  // than as having no licence). This function used to return `data.users` and
  // drop both on the floor.
  //
  // They are ATTACHED to the users array rather than wrapping it in an object:
  // all five callers - UsersView, HandleUpload, StudentPayment, Course, Profile
  // - treat the resolved value as the user list itself, and re-shaping it would
  // break every one of them for the benefit of the one that wants the extras.
  // Non-enumerable so the array still spreads, stringifies and `for...in`s
  // exactly as it did before.
  function withLicensingProgress(data) {
    const users = data.users || [];
    Object.defineProperties(users, {
      licensing_jobs: { value: data.licensing_jobs || [], enumerable: false, configurable: true },
      licensing_pending: { value: data.licensing_pending || [], enumerable: false, configurable: true },
    });
    return users;
  }

  async function RemoveUser(user_uid, entity) {
    let api;
    if (entity.entity_type === 'organization') {
      api = default_api;
    }
    else {
      api = await this.api();
    }
    try {
      const response = await api.post('/remove_user', { user_uid: user_uid, entity: entity });
      if (response.status === 200) {
        Notify.create({
          color: 'positive',
          message: `Removed user from ${entity.entity_type} successfully`,
          icon: 'save'
        })

        return response.data
      }
    }
    catch (error) {
      // This block used to swallow 5xx entirely (resolving undefined) as well as
      // returning the 4xx message string. Both callers that read `.user` off the
      // result crashed on that undefined, so propagating is strictly better and
      // matches every other converted function in this file.
      throw toStoreError(error);
    }
  }

  async function GetRecycleBin(page, rowsPerPage, sortBy, descending, filter) {
    const api = await this.api();
    const response = await api.get('/recycle_bin', {
      params:
        { query: filter, page: page, rowsPerPage: rowsPerPage, sortBy: sortBy, descending: descending }
    });

    return response.data;
  }

  async function Undelete(obj) {
    const api = await this.api();
    const response = await api.post('/undelete', { obj: obj });

    Notify.create({
      color: 'positive',
      message: `Undelete successful`,
    })
    return response.data;
  }

  async function GetProducts() {
    const api = await this.api();
    const response = await api.get(`/products`);

    return response.data;
  }

  async function GetPrices(product_id, length) {
    const api = await this.api();
    const response = await api.get(`/prices/${product_id}`,
      { params: { length: length } });

    return response.data;
  }

  function CheckPolicy(course, level) {
    let values = [];
    if (course.campus_embed && course.campus_embed.policy) {
      if (level === 'simulation' && course.campus_embed.policy.product === 'basic') {
        values.push(false);
      }
      else if (level === 'advanced' && ['basic', 'simulation'].includes(course.campus_embed.policy.product)) {
        values.push(false);
      }
      else {
        values.push(true);
      }
    }
    else if (course.program_embed && course.program_embed.policy) {
      if (level === 'simulation' && course.program_embed.policy.product === 'basic') {
        values.push(false);
      }
      else if (level === 'advanced' && ['basic', 'simulation'].includes(course.program_embed.policy.product)) {
        values.push(false);
      }
      else {
        values.push(true);
      }
    }

    if (values.some(v => v === false)) {
      return false;
    }
    if (this.org && this.org.policy) {
      if (level === 'simulation' && this.org.policy.product === 'basic') {
        return false;
      }
      else if (level === 'advanced' && ['basic', 'simulation'].includes(this.org.policy.product)) {
        return false;
      }
    }
    return true
  }

  async function StartSimulation(activity, sim_start_time, sim_name) {
    const api = await this.api();
    const response = await api.post(`/simulation/course/${activity.course_uid}/activity/${activity.uid}/start`,
      { sim_start_time: sim_start_time, timezone_offset: new Date().getTimezoneOffset(), sim_name });

    Notify.create({
      color: 'positive',
      message: `Simulation Started`,
    })
    return response.data;
  }

  async function ResetSimulation(activity, simulation_run_uid) {
    const api = await this.api();
    const response = await api.post(`/simulation/course/${activity.course_uid}/activity/${activity.uid}/${simulation_run_uid}/reset`,
      {});

    Notify.create({
      color: 'info',
      message: `Simulation Reset`,
    })
    return response.data;
  }

  async function PauseSimulation(activity, simulation_run_uid) {
    const api = await this.api();
    const response = await api.post(`/simulation/course/${activity.course_uid}/activity/${activity.uid}/${simulation_run_uid}/pause`,
      {})

    Notify.create({
      color: 'info',
      message: `Simulation Paused`,
    })
    return response.data;
  }

  async function ResumeSimulation(activity, simulation_run_uid) {
    const api = await this.api();
    const response = await api.post(`/simulation/course/${activity.course_uid}/activity/${activity.uid}/${simulation_run_uid}/resume`,
      {})

    Notify.create({
      color: 'positive',
      message: `Simulation Resumed`,
    })
    return response.data;
  }

  async function TimeWarpSimulation(activity, simulation_run_uid, time_amount) {
    const api = await this.api();
    const response = await api.post(`/simulation/course/${activity.course_uid}/activity/${activity.uid}/${simulation_run_uid}/time_warp`,
      { time_amount: time_amount })

    Notify.create({
      color: 'info',
      message: `Simulation Time Warped`,
    })
    return response.data;
  }

  async function GetSimulationRuns(course_uid, activity_uid) {
    const api = await this.api();
    const response = await api.get(`/simulation/course/${course_uid}/activity/${activity_uid}/simulation_runs`);

    return response.data;
  }

  async function GetSimulationUsers(course_uid, activity_uid, simulation_run_uid) {
    const api = await this.api();
    const response = await api.get(`/simulation/course/${course_uid}/activity/${activity_uid}/${simulation_run_uid}/users`);

    return response.data;
  }

  async function GetSimulationMessages(course_uid, activity_uid, simulation_run_uid) {
    const api = await this.api();
    const response = await api.get(`/simulation/course/${course_uid}/activity/${activity_uid}/${simulation_run_uid}/messages`);

    return response.data.messages;
  }

  async function AddSimulationUsers(course_uid, activity_uid, simulation_run_uid, users) {
    const api = await this.api();
    const response = await api.post(`/simulation/course/${course_uid}/activity/${activity_uid}/${simulation_run_uid}/add_users`, { users: users });
    Notify.create({
      color: 'positive',
      message: `Added ${users.length} users successfully`,
      icon: 'save'
    })
    return response.data.users;
  }

  async function RemoveSimulationUser(course_uid, activity_uid, simulation_run_uid, user_uid) {
    const api = await this.api();
    const response = await api.post(`/simulation/course/${course_uid}/activity/${activity_uid}/${simulation_run_uid}/remove_user`, { user_uid: user_uid });
    Notify.create({
      color: 'info',
      message: `Removed user successfully`,
      icon: 'save'
    })
    return response.data;
  }

  async function AddDevice(device) {
    const api = await this.api();
    const response = await api.post('/add_device', device);
    if (response.status === 200) {
      Notify.create({
        color: 'positive',
        message: `Device ${response.data.device.name} added successfully`,
        icon: 'save'
      });
      return response.data.device;
    }
  }

  async function GetDevices(page, rowsPerPage, sortBy, descending, filter, user_uid, organization_uid) {
    const api = await this.api();
    const response = await api.get('/get_devices', {
      params:
        { query: filter, page: page, rowsPerPage: rowsPerPage, sortBy: sortBy, descending: descending, user_uid: user_uid, organization_uid: organization_uid }
    });
    if (response.status === 200) {
      return response.data;
    }
  }

  async function GetDevicesByActivity(page, rowsPerPage, sortBy, descending, filter, activity_uid) {
    const api = await this.api();
    const response = await api.get(`/devices_by_activity`, {
      params:
        { query: filter, page: page, rowsPerPage: rowsPerPage, sortBy: sortBy, descending: descending, activity_uid: activity_uid }
    });

    if (response.status === 200) {
      return response.data;
    }
  }

  async function UpdateDevice(device) {
    const api = await this.api();
    const response = await api.post('/device', { device: device });

    if (response.status === 200) {
      Notify.create({
        color: 'positive',
        message: `Device ${response.data.device.name} updated successfully`,
        icon: 'save'
      });
      return response.data;
    }
  }

  async function AddDevicesActivity(course_uid, activity_uid, devices) {
    const api = await this.api();
    const response = await api.post(`/course/${course_uid}/activity/${activity_uid}/add_devices`, { devices: devices })

    if (response.status === 200) {
      Notify.create({
        color: 'positive',
        message: `Devices added successfully`,
        icon: 'save'
      });
      return response.data;
    }
  }

  // The Sim Control Panel's "Medication Cabinet" choice: ties the chosen
  // physical cabinet's own computer (the one its pointer names) to the sim.
  async function AddCabinetComputerActivity(course_uid, activity_uid, cabinet_uid) {
    const api = await this.api();
    const response = await api.post(`/course/${course_uid}/activity/${activity_uid}/cabinet_computer`, { cabinet_uid })
    if (response.status === 200) {
      Notify.create({
        color: 'positive',
        message: `Cabinet Device added`,
        icon: 'save'
      });
      return response.data;
    }
  }

  async function DeleteDevice(device_id) {
    const api = await this.api();
    try {
      const response = await api.delete(`/device/${device_id}`)
      Notify.create({
        color: 'info',
        message: `Device ${response.data.device.name} deleted successfully`,
        icon: 'delete'
      })
    }
    catch (error) {
      if (error.status === 404) {
        console.log('Device not found');
      }
      else {
        throw error;
      }
    }
  }

  // `for_student_selection` is trailing and optional so every existing caller
  // (the admin listings) is unchanged and keeps getting every cohort. Only the
  // student payment page passes it; it asks the backend to drop the cohorts that
  // cannot license the student - no policy, or an auto-assign policy with no codes
  // left.
  async function GetCohorts(organization_uid, program_uid, campus_uid, page, rowsPerPage, sortBy, descending, filter, show_old, for_student_selection) {
    const api = await this.api();
    const response = await api.get('/cohorts', {
      params:
      {
        organization_uid: organization_uid, program_uid: program_uid, campus_uid: campus_uid,
        query: filter, page: page, rowsPerPage: rowsPerPage, sortBy: sortBy, descending: descending,
        show_old: show_old, for_student_selection: for_student_selection
      }
    });

    return response.data;
  }

  async function GetCohort(cohort_uid) {
    const api = await this.api();
    const response = await api.get(`/cohort/${cohort_uid}`);

    return response.data;
  }

  async function UpdateCohort(cohort) {
    const api = await this.api();
    const response = await api.post('/cohort', { cohort: cohort });

    if (response.status === 200) {
      Notify.create({
        color: 'positive',
        message: `Cohort ${response.data.cohort.cohort_name} updated successfully`,
        icon: 'save'
      });
      return response.data;
    }
  }

  async function AddOrInviteCohort(cohort, entity) {
    const api = await this.api();
    const response = await api.post('/add_invite_cohort', { cohort_uid: cohort.uid, entity: entity });

    if (response.status === 200) {
      Notify.create({
        color: 'positive',
        message: `Cohort ${cohort.cohort_name} added to ${entity.entity_type} successfully`,
        icon: 'save'
      });
      // Same asynchronous licensing as the roster path above, same payload.
      return withLicensingProgress(response.data);
    }
  }

  async function DeleteCohort(cohort_uid) {
    const api = await this.api();
    const response = await api.delete(`/cohort/${cohort_uid}`)
    Notify.create({
      color: 'info',
      message: `Cohort ${response.data.cohort.name} deleted successfully`,
      icon: 'delete'
    })
  }

  async function SearchCustomers(search_term) {
    const api = await this.api();
    const response = await api.get(`/stripe_customers`, {
      params:
        { q: search_term }
    });

    return response.data;
  }

  // --- EngageBay CRM (staff only; backend/services/api/crm) -----------------
  // Each rejects on failure with the server's message: a 4xx as ApiClientError,
  // an EngageBay failure (502 "EngageBay: <status>") as the axios error, whose
  // message the CRM components read from error.response.data.message.
  async function crmRequest(method, url, options = {}) {
    try {
      const api_ = await api();
      const response = await api_.request({ method, url, ...options });
      return response.data;
    } catch (error) {
      throw toStoreError(error);
    }
  }

  const crmLinkPath = (organization_uid, entity_type, entity_uid) =>
    `/crm/links/${encodeURIComponent(organization_uid)}/${encodeURIComponent(entity_type)}/${encodeURIComponent(entity_uid)}`;

  function GetCrmLinks(organization_uid, entity_type, entity_uid) {
    return crmRequest('get', crmLinkPath(organization_uid, entity_type, entity_uid));
  }

  function SaveCrmLink(organization_uid, entity_type, entity_uid, kind, crm_id) {
    return crmRequest('post', crmLinkPath(organization_uid, entity_type, entity_uid), { data: { kind, crm_id } });
  }

  function DeleteCrmLink(organization_uid, entity_type, entity_uid, kind, crm_id) {
    return crmRequest('delete', `${crmLinkPath(organization_uid, entity_type, entity_uid)}/${encodeURIComponent(kind)}/${encodeURIComponent(crm_id)}`);
  }

  function SearchCrm(kind, q) {
    return crmRequest('get', '/crm/search', { params: { kind, q } });
  }

  // Tracks, owners, tags and custom field definitions change rarely: fetched
  // once per session and kept here. A failed fetch is not cached.
  const crm_metadata = ref(null);
  let crmMetadataPromise = null;
  function GetCrmMetadata() {
    if (crm_metadata.value) return Promise.resolve(crm_metadata.value);
    if (!crmMetadataPromise) {
      crmMetadataPromise = crmRequest('get', '/crm/metadata')
        .then(data => { crm_metadata.value = data; return data; })
        .finally(() => { crmMetadataPromise = null; });
    }
    return crmMetadataPromise;
  }

  // Unfinished guided actions (Demo Complete, Adoption Confirmed), kept here and not in the dialog so
  // closing and reopening the dialog resumes instead of making a second record. Keyed by
  // `${flow}:${entity_type}:${entity_uid}`. In memory for the session.
  const crm_drafts = ref({});
  const GetCrmDraft = (key) => crm_drafts.value[key] || null;
  function SetCrmDraft(key, draft) {
    crm_drafts.value = { ...crm_drafts.value, [key]: draft };
  }
  function ClearCrmDraft(key) {
    const { [key]: _gone, ...rest } = crm_drafts.value;
    crm_drafts.value = rest;
  }

  function GetCrmDeal(deal_id) {
    return crmRequest('get', `/crm/deal/${encodeURIComponent(deal_id)}`);
  }

  function SaveCrmDeal(deal) {
    return crmRequest('post', '/crm/deal', { data: deal });
  }

  function WinCrmDeal(deal_id) {
    return crmRequest('post', `/crm/deal/${encodeURIComponent(deal_id)}/won`);
  }

  function GetCrmCompany(company_id) {
    return crmRequest('get', `/crm/company/${encodeURIComponent(company_id)}`);
  }

  function SaveCrmCompany(company) {
    return crmRequest('post', '/crm/company', { data: company });
  }

  async function GetLicenses(organization_uid, entity_type, entity_uid, invoice_id, page, rowsPerPage, sortBy, descending, filter) {

    const api = await this.api();
    const response = await api.get(`/licenses/${organization_uid}/${entity_type}/${entity_uid}/${invoice_id}`, {
      params: {
        query: filter,
        page: page,
        rowsPerPage: rowsPerPage,
        sortBy: sortBy,
        descending: descending
      }
    });

    return response.data;
  }

  // Progress of one asynchronous licence assignment (#959). The grant runs in a
  // worker, so a screen that shows licences has to be able to watch the work
  // finish rather than leaving an administrator on a stale page.
  async function GetLicenseJob(job_uid) {
    const api = await this.api();
    const response = await api.get(`/license/job/${job_uid}`);

    return response.data.job;
  }

  async function GenerateMissingCodes(entity_type, entity_uid, invoice_id) {
    const api = await this.api();
    const response = await api.post(`/license/generate_missing_codes/${entity_type}/${entity_uid}/${invoice_id}`);

    return response.data;
  }

  async function AssignStudentCodes(entity_type, entity_uid, invoice_id) {
    const api = await this.api();
    const response = await api.post(`/license/assign_student_codes/${entity_type}/${entity_uid}/${invoice_id}`);

    return response.data;
  }

  // user_uid names whose license to remove. Without it the backend falls back
  // to the code's recorded redeemer, which is the wrong person whenever a code
  // was claimed more than once (#941) — always pass it.
  async function DeleteLicense(payment_code, user_uid) {
    const api = await this.api();
    const response = await api.delete(
      `/license/${payment_code}`,
      user_uid ? {params: {user_uid: user_uid}} : undefined
    );

    return response.data;
  }

  async function DeletePaymentCode(payment_code) {
    const api = await this.api();
    const response = await api.delete(`/payment_code/${payment_code}`);

    return response.data;
  }

  async function AssignLicense() {

    const api = await this.api();
    // `license_pending` means a grant this student already has coming is still
    // in flight (a cold worker takes a couple of seconds). StudentPayment.vue
    // waits that out on the page with its overlay up and calls again, so the
    // interceptor must not toast it - the visitor would get "try again shortly"
    // while the page is already doing exactly that (#634). Every other failure
    // on this call toasts as before.
    const response = await api.post(`/license/assign`, undefined, { silent_reasons: ['license_pending'] });

    Notify.create({
      color: 'positive',
      message: `License assigned successfully`,
      icon: 'save'
    });
    return response.data;
  }

  async function GetPaymentIntent(license) {
    const api = await this.api();
    const response = await api.get(`/license/payment_intent/${license.payment_intent}`);

    return response.data;
  }

  async function UpdateLicense(user, license) {

    const api = await this.api();
    const response = await api.post(`/license/user/${user.uid}`, { license: license });

    Notify.create({
      color: 'positive',
      message: `License updated successfully`,
      icon: 'save'
    });
    return response.data;
  }

  async function GenerateLicense(user_uid, license_length) {
    const api = await this.api();
    const response = await api.post(`/license/generate`, { user_uid, license_length });

    Notify.create({
      color: 'positive',
      message: `License generated successfully`,
      icon: 'check_circle'
    });
    return response.data;
  }

  async function GetIntegration(integration_type, integration_id) {

    const api = await this.api();
    const response = await api.get(`/integration/${integration_type}/${integration_id}`);

    return response.data;
  }

  async function GetIntegrations(entity_type, entity_uid, integration_type, organization_uid) {

    const api = await this.api();
    const response = await api.get(`/integrations/${entity_type}/${entity_uid}`, {
      params: {
        integration_type: integration_type,
        organization_uid: organization_uid
      }
    });

    return response.data;
  }

  async function UpdateIntegration(integration) {

    const api = await this.api();
    const response = await api.post(`/integration`, { integration: integration });

    Notify.create({
      color: 'positive',
      message: `Integration updated successfully`,
      icon: 'save'
    });
    return response.data;
  }

  async function DeleteIntegration(entity_type, integration_id) {
    const api = await this.api();
    const response = await api.delete(`/integration/${entity_type}/${integration_id}`);

    Notify.create({
      color: 'info',
      message: `Integration deleted successfully`,
      icon: 'delete'
    });
    return response.data;
  }

  // Rubric Template Methods
  async function GetRubricTemplates(page, rowsPerPage, sortBy, descending, filter, filtering) {
    try {
      const api = await this.api();
      const response = await api.get(`/api/rubric-templates`, {
        params: {
          query: filter,
          page: page,
          page_size: rowsPerPage,
          sort_by: sortBy,
          descending: descending,
          filtering: filtering
        }
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      throw toStoreError(error);
    }
  }

  async function GetRubricTemplate(uid) {
    try {
      const api = await this.api();
      const response = await api.get(`/api/rubric-templates/${uid}`);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      throw toStoreError(error);
    }
  }

  async function CreateRubricTemplate(rubric) {
    try {
      const api = await this.api();
      const response = await api.post(`/api/rubric-templates`, rubric);
      if (response.status === 200 || response.status === 201) {
        return response.data;
      }
    } catch (error) {
      throw toStoreError(error);
    }
  }

  async function UpdateRubricTemplate(uid, rubric) {
    try {
      const api = await this.api();
      const response = await api.put(`/api/rubric-templates/${uid}`, rubric);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      throw toStoreError(error);
    }
  }

  async function DeleteRubricTemplate(uid) {
    try {
      const api = await this.api();
      const response = await api.delete(`/api/rubric-templates/${uid}`);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      throw toStoreError(error);
    }
  }

  async function CopyRubricTemplate(uid) {
    try {
      const apiInstance = await this.api();
      if (!apiInstance) {
        return 'Not authenticated';
      }
      // Get the original rubric using the same pattern as GetRubricTemplate
      const original = await GetRubricTemplate.call(this, uid);
      if (!original || typeof original === 'string') {
        return original || 'Failed to get rubric template';
      }

      // Create a copy with modified name
      const copy = {
        name: `${original.name} (Copy)`,
        description: original.description,
        performance_levels: original.performance_levels,
        criteria: original.criteria
      };

      const createResponse = await apiInstance.post(`/api/rubric-templates`, copy);
      if (createResponse.status === 200 || createResponse.status === 201) {
        copied_rubric.value = createResponse.data;
        return createResponse.data;
      }
    } catch (error) {
      throw toStoreError(error);
    }
  }

  const setOrganization = (organization) => {
    console.log('setOrganization: Setting organization', organization);
    console.log('setOrganization: Old org:', sessionStore.organization);
    sessionStore.organization = organization;
    console.log('setOrganization: New org:', sessionStore.organization);
    // Clear any cached API instances when org changes
    api_instance.value = null;
    api_region.value = null;
  };

  async function GetChangelog() {
    const response = await default_api.get('/changelog');
    if (response.status === 200) {
      return response.data;
    }
  }

  async function CreateChangelog(entry) {
    const response = await default_api.post('/changelog', entry);
    if (response.status === 200) {
      Notify.create({
        color: 'positive',
        message: 'Changelog entry saved',
        icon: 'save'
      });
      return response.data.entry;
    }
  }

  async function DeleteChangelog(uid) {
    const response = await default_api.delete(`/changelog/${uid}`);
    if (response.status === 200) {
      Notify.create({
        color: 'positive',
        message: 'Changelog entry deleted',
        icon: 'delete'
      });
      return response.data;
    }
  }

  async function MarkChangelogSeen() {
    const response = await default_api.post('/changelog/seen');
    if (response.status === 200) {
      return response.data;
    }
  }

  return {
    org,
    api_instance,
    api_region,
    activity,
    course,
    copied_rubric,
    session,
    api,
    setOrganization,
    GetOrg,
    GetOrganization,
    UpdateOrganization,
    GetPurchases,
    MakePurchase,
    GetQuote,
    DeleteOrganization,
    GetOrgLoginOptions,
    ParseSAML,
    UpdateProgram,
    DeleteProgram,
    UpdateCampus,
    DeleteCampus,
    GetProgram,
    GetCampus,
    GetCampuses,
    GetCabinets,
    CreateCabinet,
    DeleteCabinet,
    CopyCabinet,
    GetPrograms,
    GetCourses,
    get_product_prices_for_user,
    GetTemplateCourses,
    GetActivities,
    GetTemplateActivities,
    UpdateCourse,
    DeleteCourse,
    recentlyDeletedCourseUids,
    UpdateActivity,
    UpdateActivityVisibility,
    DeleteActivity,
    SubmitActivity,
    StartNewAttempt,
    ValidateSubmission,
    AutoSaveActivity,
    GetSubmissionStatus,
    GetAttemptInfo,
    GetCourse,
    GetActivity,
    CopyActivity,
    CopyCourse,
    MakeTemplateActivity,
    MakeTemplateCourse,
    AddCourse,
    GetStudentActivity,
    GetStudentSubmissions,
    GetInstructorSubmissions,
    DownloadInstructorSubmissionsCSV,
    ReopenAssignment,
    BulkReopenAssignments,
    GrantAdditionalAttempts,
    RetryLTIGrade,
    InvalidateAttempt,
    BulkGrantAttempts,
    GetGradingReport,
    SaveFieldScore,
    GetFieldScores,
    GetSimulationData,
    GetStudentDashboardActivities,
    AddOrInviteUsers,
    RemoveUser,
    GetRecycleBin,
    Undelete,
    GetProducts,
    GetPrices,
    CheckPolicy,
    StartSimulation,
    ResetSimulation,
    PauseSimulation,
    ResumeSimulation,
    TimeWarpSimulation,
    GetSimulationRuns,
    GetSimulationUsers,
    GetSimulationMessages,
    AddSimulationUsers,
    RemoveSimulationUser,
    AddDevice,
    GetDevices,
    GetDevicesByActivity,
    UpdateDevice,
    AddDevicesActivity,
    AddCabinetComputerActivity,
    DeleteDevice,
    GetCohorts,
    GetCohort,
    UpdateCohort,
    DeleteCohort,
    AddOrInviteCohort,
    SearchCustomers,
    crm_metadata,
    GetCrmLinks,
    SaveCrmLink,
    DeleteCrmLink,
    SearchCrm,
    GetCrmMetadata,
    crm_drafts,
    GetCrmDraft,
    SetCrmDraft,
    ClearCrmDraft,
    GetCrmDeal,
    SaveCrmDeal,
    WinCrmDeal,
    GetCrmCompany,
    SaveCrmCompany,
    GetLicenses,
    GetLicenseJob,
    GenerateMissingCodes,
    AssignStudentCodes,
    DeleteLicense,
    DeletePaymentCode,
    AssignLicense,
    GetPaymentIntent,
    UpdateLicense,
    GenerateLicense,
    GetIntegration,
    GetIntegrations,
    UpdateIntegration,
    DeleteIntegration,
    GetRubricTemplates,
    GetRubricTemplate,
    CreateRubricTemplate,
    UpdateRubricTemplate,
    DeleteRubricTemplate,
    CopyRubricTemplate,
    GetChangelog,
    CreateChangelog,
    DeleteChangelog,
    MarkChangelogSeen
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRegionStore, import.meta.hot))
}
